/**
 * OP_NET native ABI definitions for MotoSettle contracts.
 *
 * These map directly to the contract selectors in
 * contracts-opnet/src/contracts/EscrowCore.ts and MotoSettleRouter.ts.
 */

import { ABIDataTypes } from "@btc-vision/transaction";
import { BitcoinAbiTypes, type BitcoinInterfaceAbi } from "opnet";

// ─── EscrowCore ABI ──────────────────────────────────────────────────────────

export const ESCROW_CORE_OPNET_ABI: BitcoinInterfaceAbi = [
  // createEscrow(address,address,address,uint256,uint64,uint64) → uint256
  {
    name: "createEscrow",
    type: BitcoinAbiTypes.Function,
    inputs: [
      { name: "seller", type: ABIDataTypes.ADDRESS },
      { name: "arbitrator", type: ABIDataTypes.ADDRESS },
      { name: "token", type: ABIDataTypes.ADDRESS },
      { name: "amount", type: ABIDataTypes.UINT256 },
      { name: "fundDeadline", type: ABIDataTypes.UINT64 },
      { name: "tradeDeadline", type: ABIDataTypes.UINT64 },
    ],
    outputs: [{ name: "escrowId", type: ABIDataTypes.UINT256 }],
  },

  // fundEscrow(uint256,uint256)
  {
    name: "fundEscrow",
    type: BitcoinAbiTypes.Function,
    inputs: [
      { name: "escrowId", type: ABIDataTypes.UINT256 },
      { name: "motoBalance", type: ABIDataTypes.UINT256 },
    ],
    outputs: [],
  },

  // activateEscrow(uint256)
  {
    name: "activateEscrow",
    type: BitcoinAbiTypes.Function,
    inputs: [{ name: "escrowId", type: ABIDataTypes.UINT256 }],
    outputs: [],
  },

  // releaseFunds(uint256)
  {
    name: "releaseFunds",
    type: BitcoinAbiTypes.Function,
    inputs: [{ name: "escrowId", type: ABIDataTypes.UINT256 }],
    outputs: [],
  },

  // raiseDispute(uint256)
  {
    name: "raiseDispute",
    type: BitcoinAbiTypes.Function,
    inputs: [{ name: "escrowId", type: ABIDataTypes.UINT256 }],
    outputs: [],
  },

  // resolveDispute(uint256,uint64)
  {
    name: "resolveDispute",
    type: BitcoinAbiTypes.Function,
    inputs: [
      { name: "escrowId", type: ABIDataTypes.UINT256 },
      { name: "buyerPct", type: ABIDataTypes.UINT64 },
    ],
    outputs: [],
  },

  // expireEscrow(uint256)
  {
    name: "expireEscrow",
    type: BitcoinAbiTypes.Function,
    inputs: [{ name: "escrowId", type: ABIDataTypes.UINT256 }],
    outputs: [],
  },

  // getEscrow(uint256) → tuple (read-only)
  {
    name: "getEscrow",
    type: BitcoinAbiTypes.Function,
    constant: true,
    inputs: [{ name: "escrowId", type: ABIDataTypes.UINT256 }],
    outputs: [
      { name: "buyer", type: ABIDataTypes.ADDRESS },
      { name: "seller", type: ABIDataTypes.ADDRESS },
      { name: "arbitrator", type: ABIDataTypes.ADDRESS },
      { name: "token", type: ABIDataTypes.ADDRESS },
      { name: "amount", type: ABIDataTypes.UINT256 },
      { name: "fee", type: ABIDataTypes.UINT256 },
      { name: "fundDeadline", type: ABIDataTypes.UINT64 },
      { name: "tradeDeadline", type: ABIDataTypes.UINT64 },
      { name: "createdAt", type: ABIDataTypes.UINT64 },
      { name: "state", type: ABIDataTypes.UINT8 },
      { name: "buyerResolved", type: ABIDataTypes.UINT256 },
      { name: "sellerResolved", type: ABIDataTypes.UINT256 },
    ],
  },

  // escrowCount() → uint256 (read-only)
  {
    name: "escrowCount",
    type: BitcoinAbiTypes.Function,
    constant: true,
    inputs: [],
    outputs: [{ name: "count", type: ABIDataTypes.UINT256 }],
  },
];

// ─── OP20 Token ABI ──────────────────────────────────────────────────────────

export const OP20_ABI: BitcoinInterfaceAbi = [
  // balanceOf(address) → uint256
  {
    name: "balanceOf",
    type: BitcoinAbiTypes.Function,
    constant: true,
    inputs: [{ name: "owner", type: ABIDataTypes.ADDRESS }],
    outputs: [{ name: "balance", type: ABIDataTypes.UINT256 }],
  },
  // allowance(address,address) → uint256
  {
    name: "allowance",
    type: BitcoinAbiTypes.Function,
    constant: true,
    inputs: [
      { name: "owner", type: ABIDataTypes.ADDRESS },
      { name: "spender", type: ABIDataTypes.ADDRESS },
    ],
    outputs: [{ name: "remaining", type: ABIDataTypes.UINT256 }],
  },
  // increaseAllowance(address,uint256) — OP20 standard (replaces ERC20 approve). Returns empty.
  {
    name: "increaseAllowance",
    type: BitcoinAbiTypes.Function,
    inputs: [
      { name: "spender", type: ABIDataTypes.ADDRESS },
      { name: "amount", type: ABIDataTypes.UINT256 },
    ],
    outputs: [],
  },
  // decreaseAllowance(address,uint256) — returns empty
  {
    name: "decreaseAllowance",
    type: BitcoinAbiTypes.Function,
    inputs: [
      { name: "spender", type: ABIDataTypes.ADDRESS },
      { name: "amount", type: ABIDataTypes.UINT256 },
    ],
    outputs: [],
  },
  // decimals() → uint8
  {
    name: "decimals",
    type: BitcoinAbiTypes.Function,
    constant: true,
    inputs: [],
    outputs: [{ name: "decimals", type: ABIDataTypes.UINT8 }],
  },
  // symbol() → string
  {
    name: "symbol",
    type: BitcoinAbiTypes.Function,
    constant: true,
    inputs: [],
    outputs: [{ name: "symbol", type: ABIDataTypes.STRING }],
  },
];

// ─── MotoSettleRouter ABI ────────────────────────────────────────────────────

export const ROUTER_OPNET_ABI: BitcoinInterfaceAbi = [
  // createAndFundEscrow(address,address,address,uint256,uint64,uint64,uint256) → uint256
  {
    name: "createAndFundEscrow",
    type: BitcoinAbiTypes.Function,
    inputs: [
      { name: "seller", type: ABIDataTypes.ADDRESS },
      { name: "arbitrator", type: ABIDataTypes.ADDRESS },
      { name: "token", type: ABIDataTypes.ADDRESS },
      { name: "amount", type: ABIDataTypes.UINT256 },
      { name: "fundDeadline", type: ABIDataTypes.UINT64 },
      { name: "tradeDeadline", type: ABIDataTypes.UINT64 },
      { name: "motoBalance", type: ABIDataTypes.UINT256 },
    ],
    outputs: [{ name: "escrowId", type: ABIDataTypes.UINT256 }],
  },
];

// ─── ArbitratorRegistry ABI ─────────────────────────────────────────────────

export const ARB_REGISTRY_OPNET_ABI: BitcoinInterfaceAbi = [
  // getInfo(address) → (bool registered, bool active, uint64 feeRateBps, uint64 casesHandled, uint64 casesWon, uint64 casesLost, uint64 avgResolution)
  {
    name: "getInfo",
    type: BitcoinAbiTypes.Function,
    constant: true,
    inputs: [{ name: "arb", type: ABIDataTypes.ADDRESS }],
    outputs: [
      { name: "registered", type: ABIDataTypes.BOOL },
      { name: "active", type: ABIDataTypes.BOOL },
      { name: "feeRateBps", type: ABIDataTypes.UINT64 },
      { name: "casesHandled", type: ABIDataTypes.UINT64 },
      { name: "casesWon", type: ABIDataTypes.UINT64 },
      { name: "casesLost", type: ABIDataTypes.UINT64 },
      { name: "avgResolution", type: ABIDataTypes.UINT64 },
    ],
  },
  // isActive(address) → bool
  {
    name: "isActive",
    type: BitcoinAbiTypes.Function,
    constant: true,
    inputs: [{ name: "arb", type: ABIDataTypes.ADDRESS }],
    outputs: [{ name: "active", type: ABIDataTypes.BOOL }],
  },
  // register(uint64) → bool
  {
    name: "register",
    type: BitcoinAbiTypes.Function,
    inputs: [{ name: "feeRateBps", type: ABIDataTypes.UINT64 }],
    outputs: [{ name: "success", type: ABIDataTypes.BOOL }],
  },
  // maxArbitratorFeeRateBps() → uint256
  {
    name: "maxArbitratorFeeRateBps",
    type: BitcoinAbiTypes.Function,
    constant: true,
    inputs: [],
    outputs: [{ name: "maxFeeRate", type: ABIDataTypes.UINT256 }],
  },
];

// ─── UserReputation ABI ─────────────────────────────────────────────────────

export const USER_REPUTATION_OPNET_ABI: BitcoinInterfaceAbi = [
  // getStats(address) → (completedTrades, lifetimeVolume, disputesRaised, disputesWon, disputesLost, repScore, lastUpdated)
  {
    name: "getStats",
    type: BitcoinAbiTypes.Function,
    constant: true,
    inputs: [{ name: "user", type: ABIDataTypes.ADDRESS }],
    outputs: [
      { name: "completedTrades", type: ABIDataTypes.UINT256 },
      { name: "lifetimeVolume", type: ABIDataTypes.UINT256 },
      { name: "disputesRaised", type: ABIDataTypes.UINT256 },
      { name: "disputesWon", type: ABIDataTypes.UINT256 },
      { name: "disputesLost", type: ABIDataTypes.UINT256 },
      { name: "repScore", type: ABIDataTypes.UINT256 },
      { name: "lastUpdated", type: ABIDataTypes.UINT256 },
    ],
  },
  // getScore(address) → uint32
  {
    name: "getScore",
    type: BitcoinAbiTypes.Function,
    constant: true,
    inputs: [{ name: "user", type: ABIDataTypes.ADDRESS }],
    outputs: [{ name: "score", type: ABIDataTypes.UINT32 }],
  },
];

// ─── FeePolicy ABI (partial — reads only) ───────────────────────────────────

export const FEE_POLICY_OPNET_ABI: BitcoinInterfaceAbi = [
  // computeFee(address,uint256,uint256) → uint256
  {
    name: "computeFee",
    type: BitcoinAbiTypes.Function,
    constant: true,
    inputs: [
      { name: "user", type: ABIDataTypes.ADDRESS },
      { name: "amount", type: ABIDataTypes.UINT256 },
      { name: "motoBalance", type: ABIDataTypes.UINT256 },
    ],
    outputs: [{ name: "fee", type: ABIDataTypes.UINT256 }],
  },
];
